import { useState, type CSSProperties, type ReactNode } from "react";

/**
 * iJewel's configurator bar: one pill per material slot along the bottom of the
 * canvas, and above it a small panel with the ACTIVE slot's choices as round
 * swatches. Shared by the studio (where the designer edits it — "+" to add a
 * choice, × to remove one) and the shared link (where a shopper only picks).
 *
 * Presentational only. It knows nothing about the viewer, the library or the
 * Vault; the two hosts own what a click does.
 */

export interface SlotBarSlot {
  key: string;
  label: string;
  /** Tooltip — the raw layer name when the label is a tidied one. */
  title?: string;
}

export interface SlotBarChoice {
  key: string;
  name: string;
  /** The swatch's single background property (see `optionFill`). */
  fill: CSSProperties;
  active: boolean;
  /** This choice is being put on the model right now. */
  busy?: boolean;
}

type Tone = "studio" | "glass";

/**
 * The two looks. `studio` is the editor's: white panel, ink text, the one gold
 * accent, the warm `ed-bg` track that the top bar's Basic|Advanced switch uses
 * (so an active pill reads as white on it, as in the reference). `glass` is the
 * shared link's frosted panel, ringed in the embed's own accent.
 */
const TONES: Record<
  Tone,
  {
    panel: string;
    track: string;
    pill: string;
    pillOn: string;
    pillOff: string;
    caption: string;
    ringOn: string;
    add: string;
    remove: string;
    size: string;
  }
> = {
  studio: {
    panel: "rounded-2xl border border-ed-border bg-ed-panel shadow-[0_6px_20px_rgba(0,0,0,0.08)]",
    track: "rounded-full border border-ed-border bg-ed-bg p-1 shadow-[0_6px_20px_rgba(0,0,0,0.08)]",
    pill: "h-8 rounded-full px-3.5 text-[13px] font-medium",
    pillOn: "bg-ed-panel text-ed-text shadow-[0_1px_3px_rgba(0,0,0,0.14)]",
    pillOff: "text-ed-text-2 hover:text-ed-text",
    caption: "text-[12px] leading-4 text-ed-text-2",
    ringOn: "0 0 0 2px #fff, 0 0 0 4px #946f2e",
    add: "border border-dashed border-ed-line text-ed-text-2 hover:border-ed-text hover:text-ed-text",
    remove: "border border-ed-border bg-ed-panel text-ed-text-2 hover:text-ed-text",
    size: "h-10 w-10",
  },
  glass: {
    panel: "glass-panel rounded-[22px] shadow-pop",
    track: "glass-panel rounded-full p-1 shadow-pop",
    pill: "h-8 rounded-full px-3.5 text-[13px] font-medium",
    pillOn: "bg-white text-ink-900 shadow-[0_1px_3px_rgba(0,0,0,0.16)]",
    pillOff: "text-ink-600 hover:text-ink-900",
    caption: "text-[12px] leading-4 text-ink-600",
    ringOn: "0 0 0 2px #fff, 0 0 0 4px var(--accent, #a0793d)",
    add: "border border-dashed border-ink-300 text-ink-600",
    remove: "border border-black/10 bg-white text-ink-600",
    size: "h-9 w-9",
  },
};

const RING_OFF = "inset 0 0 0 1px rgba(0,0,0,0.12)";

function PlusGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CrossGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function SlotBar({
  tone,
  slots,
  activeSlot,
  onSlot,
  choices,
  onChoice,
  onAdd,
  addOpen = false,
  onRemove,
  disabled = false,
  emptyText,
  header,
  trailing,
  ariaLabel = "Material choices",
  activeCaption = (name) => name,
}: {
  tone: Tone;
  slots: SlotBarSlot[];
  activeSlot: string | null;
  onSlot: (key: string) => void;
  /** The ACTIVE slot's choices. */
  choices: SlotBarChoice[];
  onChoice: (key: string) => void;
  /** Editing: a "+" swatch at the end of the row. */
  onAdd?: () => void;
  /** The picker the "+" opens is showing. */
  addOpen?: boolean;
  /** Editing: an × on each swatch. */
  onRemove?: (key: string) => void;
  disabled?: boolean;
  /** Said beside the "+" while the slot has no choices yet. */
  emptyText?: string;
  /** Above the swatches (the shared link's name + price). */
  header?: ReactNode;
  /** After the pills (the studio's Save). */
  trailing?: ReactNode;
  ariaLabel?: string;
  /** What the caption says about the ringed choice when nothing is hovered. */
  activeCaption?: (name: string) => string;
}) {
  const t = TONES[tone];
  const [hover, setHover] = useState<string | null>(null);
  const active = choices.find((c) => c.active);
  const hovered = choices.find((c) => c.key === hover);
  const caption = hovered ? hovered.name : active ? activeCaption(active.name) : undefined;
  const showPanel = !!header || choices.length > 0 || !!onAdd;
  const slotLabel = slots.find((s) => s.key === activeSlot)?.label ?? "";

  return (
    <div className="flex max-w-full flex-col items-center gap-2" data-config-bar={tone}>
      {showPanel ? (
        <div className={`pointer-events-auto flex max-w-full flex-col gap-2 px-3 py-2.5 ${t.panel}`} data-config-choices>
          {header}
          <div className="flex max-w-full justify-center">
          <div
            role="radiogroup"
            aria-label={slotLabel ? `${slotLabel} choices` : ariaLabel}
            className="flex max-w-full items-center gap-2.5 overflow-x-auto p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {choices.map((c) => (
              <div key={c.key} className="group relative shrink-0">
                <button
                  type="button"
                  role="radio"
                  aria-checked={c.active}
                  aria-label={c.name}
                  aria-busy={c.busy || undefined}
                  title={c.name}
                  disabled={disabled}
                  onClick={() => onChoice(c.key)}
                  onMouseEnter={() => setHover(c.key)}
                  onMouseLeave={() => setHover((h) => (h === c.key ? null : h))}
                  onFocus={() => setHover(c.key)}
                  onBlur={() => setHover((h) => (h === c.key ? null : h))}
                  className={`relative block shrink-0 rounded-full bg-cover bg-center transition-transform hover:scale-[1.06] disabled:cursor-not-allowed disabled:opacity-60 ${t.size}`}
                  style={{ ...c.fill, boxShadow: c.active ? t.ringOn : RING_OFF }}
                  data-config-option={c.key}
                >
                  {c.busy ? <span className="absolute inset-0 animate-pulse rounded-full bg-white/50" /> : null}
                </button>
                {onRemove ? (
                  <button
                    type="button"
                    onClick={() => onRemove(c.key)}
                    disabled={disabled}
                    title={`Remove ${c.name}`}
                    aria-label={`Remove ${c.name}`}
                    className={`absolute -right-1.5 -top-1.5 grid h-[18px] w-[18px] place-items-center rounded-full opacity-0 shadow-[0_1px_2px_rgba(0,0,0,0.12)] transition-opacity focus-visible:opacity-100 disabled:hidden group-hover:opacity-100 [@media(hover:none)]:opacity-100 ${t.remove}`}
                    data-config-remove={c.key}
                  >
                    <CrossGlyph />
                  </button>
                ) : null}
              </div>
            ))}
            {onAdd ? (
              <button
                type="button"
                onClick={onAdd}
                disabled={disabled}
                title="Add a choice"
                aria-label="Add a choice"
                aria-expanded={addOpen}
                className={`grid shrink-0 place-items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${t.size} ${t.add}`}
                style={addOpen ? { boxShadow: t.ringOn } : undefined}
                data-config-add
              >
                <PlusGlyph />
              </button>
            ) : null}
            {choices.length === 0 && emptyText ? (
              <span className={`max-w-[220px] pr-1 ${t.caption}`}>{emptyText}</span>
            ) : null}
          </div>
          </div>
          {caption ? (
            <span className={`-mt-1 truncate text-center ${t.caption}`} aria-live="polite" data-config-caption>
              {caption}
            </span>
          ) : null}
        </div>
      ) : null}
      {slots.length > 0 ? (
        <div className={`pointer-events-auto flex max-w-full items-center gap-1 ${t.track}`}>
          <div role="tablist" aria-label={ariaLabel} className="flex min-w-0 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {slots.map((s) => {
              const on = s.key === activeSlot;
              return (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  title={s.title ?? s.label}
                  onClick={() => onSlot(s.key)}
                  className={`shrink-0 whitespace-nowrap transition-colors ${t.pill} ${on ? t.pillOn : t.pillOff}`}
                  data-config-slot={s.key}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
